import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const URL_PARAM_PAGE = 'page';

const useLeadsTableInteractions = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [internalUsers, setInternalUsers] = useState([]);

    // Pagination State
    const getInitialPage = useCallback(() => {
        const pageFromUrl = searchParams.get(URL_PARAM_PAGE);
        return pageFromUrl ? parseInt(pageFromUrl, 10) : 1;
    }, [searchParams]);
    const [currentPage, setCurrentPage] = useState(getInitialPage);
    const [itemsPerPage, setItemsPerPage] = useState(50);

    // Sorting State (for API and client-side)
    const [sortBy, setSortBy] = useState('createdAt'); // API sort column
    const [sort, setSort] = useState('DESC'); // API sort direction
    const [sortByTitle, setSortByTitle] = useState(''); // Client-side sort column
    const [sortByOrder, setSortByOrder] = useState(''); // Client-side sort direction

    // Selection State
    const [selectedItems, setSelectedItems] = useState([]); // Array of {id, status}
    const [checkedAll, setCheckedAll] = useState(false);

    // Derived State for Display
    const [displayUsers, setDisplayUsers] = useState([]);

    const setFetchedUsers = useCallback((users) => {
        setInternalUsers(users || []);
    }, []);

    const updateSearchParamsHelper = useCallback((key, value, removeIfNull = true) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (value === null || value === undefined || (removeIfNull && String(value) === '1' && key === URL_PARAM_PAGE)) {
                newParams.delete(key);
            } else {
                newParams.set(key, String(value));
            }
            return newParams;
        });
    }, [setSearchParams]);


    // Effect to initialize currentPage from URL on mount and when searchParams change externally
    useEffect(() => {
        const pageNumber = getInitialPage();
        if (currentPage !== pageNumber) {
            setCurrentPage(pageNumber);
        }
    }, [searchParams, currentPage, getInitialPage]);

    // Pagination Handlers
    const paginate = useCallback((pageNumber) => {
        setCurrentPage(pageNumber);
        updateSearchParamsHelper(URL_PARAM_PAGE, pageNumber);
    }, [updateSearchParamsHelper]);

    const handleItemsPerPageChange = useCallback((num) => {
        setItemsPerPage(num);
        setCurrentPage(1); // Reset to first page
        updateSearchParamsHelper(URL_PARAM_PAGE, 1); // Update URL to page 1 or remove if 1
    }, [updateSearchParamsHelper]);

    // Sorting Handler
    const handleSortColumn = useCallback((columnName) => {
        // If it's a column that supports client-side sorting, set those states
        if (columnName === 'name' || columnName === 'email' || columnName === 'phone' || columnName === 'lead_status_title' || columnName === 'last_activity_date_formatted' || columnName === 'created_at_formatted') { // Example client-sortable columns
            setSortByTitle(columnName);
            setSortByOrder(prevOrder => (sortByTitle === columnName && prevOrder === 'asc' ? 'desc' : 'asc'));
        } else {
            // For API sorting
            setSortByTitle(''); // Clear client sort
            setSortByOrder('');
            if (sortBy === columnName) {
                setSort(prevSortDir => (prevSortDir === 'ASC' ? 'DESC' : 'ASC'));
            } else {
                setSortBy(columnName);
                setSort('ASC');
            }
        }
        setCurrentPage(1); // Reset to first page on sort
        updateSearchParamsHelper(URL_PARAM_PAGE, 1); // Update URL to page 1 or remove if 1
    }, [sortBy, sort, sortByTitle, updateSearchParamsHelper]);


    // Client-side Sorting Logic
    useEffect(() => {
        let processedList = internalUsers || [];
        if (sortByTitle && processedList.length > 0) {
            processedList = [...processedList].sort((a, b) => {
                const titleA = a[sortByTitle];
                const titleB = b[sortByTitle];
                if (titleA === null || titleA === undefined) return sortByOrder === 'asc' ? 1 : -1;
                if (titleB === null || titleB === undefined) return sortByOrder === 'asc' ? -1 : 1;
                const valA = String(titleA ?? '');
                const valB = String(titleB ?? '');
                return sortByOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            });
        }
        setDisplayUsers(processedList);
    }, [internalUsers, sortByTitle, sortByOrder]);


    // Selection Handlers
    const onSelectChange = useCallback((event, itemId, itemStatus) => {
        setSelectedItems((currentSelectedItems) => {
            if (event.target.checked) {
                // Add item only if it's not already selected, comparing IDs as strings
                if (!currentSelectedItems.some(item => String(item.id) === String(itemId))) {
                    return [...currentSelectedItems, { id: itemId, status: itemStatus }];
                }
                return currentSelectedItems; // Item already exists, return current selection
            } else {
                // Remove item, comparing IDs as strings
                return currentSelectedItems.filter((item) => String(item.id) !== String(itemId));
            }
        });
    }, []);

    const selectorCheck = useCallback((event) => {
        const checked = event.currentTarget.checked;
        // When checking "select all": Ensure newSelectedItems is populated from the current internalUsers.
        // When unchecking "select all": Ensure selectedItems is set to [].
        if (checked) {
            const currentLeads = internalUsers || [];
            const newSelectedItems = currentLeads.map((item) => ({
                id: item.id,
                status: item.lead_status || 'N/A', // Fallback for status
            }));
            setSelectedItems(newSelectedItems);
        } else {
            setSelectedItems([]);
        }
        setCheckedAll(checked); // Direct set for immediate UI feedback
    }, [internalUsers]);

    // Effect to prune selectedItems when internalUsers changes
    useEffect(() => {
        setSelectedItems(prevSelectedItems => {
            if (!internalUsers || internalUsers.length === 0) {
                return []; // If no internal users, clear selection
            }
            const newSelectedItems = prevSelectedItems.filter(selItem =>
                internalUsers.some(internalUser => String(internalUser.id) === String(selItem.id))
            );
            // Only update if the array reference or content actually changes
            if (newSelectedItems.length !== prevSelectedItems.length ||
                !newSelectedItems.every((item, index) => item.id === prevSelectedItems[index]?.id)) {
                return newSelectedItems;
            }
            return prevSelectedItems;
        });
    }, [internalUsers]); // Run when internalUsers changes

    // Refined Effect to update checkedAll based on selectedItems and internalUsers
    useEffect(() => {
        if (!internalUsers || internalUsers.length === 0) {
            if (checkedAll) setCheckedAll(false); // Only update if different
            return;
        }
        // After the pruning effect, selectedItems is a subset of internalUsers.
        // So, if their lengths are equal (and > 0), all are selected.
        const newCheckedAll = internalUsers.length > 0 && selectedItems.length === internalUsers.length;

        if (checkedAll !== newCheckedAll) {
            setCheckedAll(newCheckedAll);
        }
    }, [selectedItems, internalUsers, checkedAll]);


    return {
        // Pagination
        currentPage,
        setCurrentPage: paginate, // Expose paginate as setCurrentPage for useLeadsListFilters
        itemsPerPage,
        paginate,
        handleItemsPerPageChange,

        // Sorting
        sortBy, // For API
        sort,   // For API
        handleSortColumn, // Handles both API and client sorting initiation

        // Selection
        selectedItems,
        setSelectedItems, // Returning setter as per instructions
        checkedAll,
        onSelectChange,
        selectorCheck,

        // Data
        displayUsers, // Sorted list for display
        setFetchedUsers, // Expose setter for internalUsers
    };
};

export default useLeadsTableInteractions;